import { Button } from '@equitymultiple/react-eui';
import { graphql, PageProps } from 'gatsby';
import { Seo } from 'gatsby-plugin-wpgraphql-seo';
import React, { useEffect, useState } from 'react';

import ArticleCard from '../../components/ArticleCard/ArticleCard';
import Carousel from '../../components/Carousel/Carousel';
import Contact from '../../components/Contact/Contact';
import Layout from '../../components/layout';
import SvgProjector from '../../components/SvgProjector/SvgProjector';
import { Routes } from '../../constants';
import useUserData from '../../hooks/useUserData';
import { Post } from '../../types';
import { useFlexSearch } from '../../utils';
import * as styles from './Blog.module.scss';
import { BlogFilter } from './components/BlogFilter/BlogFilter';
import ResultsCountDisplay from './components/ResultsCount';
import SearchResults from './components/SearchResults/SearchResults';

const BlogTemplate: React.FC<PageProps<Queries.BlogByIdQuery>> = ({
  data,
  location,
}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const { userData } = useUserData();

  const [input, setInput] = useState('');

  const { results } = useFlexSearch<Post>(
    query,
    data.localSearchPosts?.publicIndexURL || '',
    data.localSearchPosts?.publicStoreURL || '',
  );

  // This useEffect handles the situation where users search all posts from a page that isn't the root /blog page e.g. https://equitymultiple.com/blog/category/market-commentary
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchInput = params.get('search');
    setQuery(searchInput);
    //Remove param so we have a clean url after the search is performed
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const featuredPost = data.featuredPost?.nodes[0] || data.allWpPost.nodes[0];
  const editorsPickPosts = data.editorsPickPosts?.nodes || [
    data.allWpPost.nodes[1],
    data.allWpPost.nodes[2],
  ];
  const popularPosts = data.popularPosts?.nodes;

  type ResourcesType = {
    header: string;
    link: string;
    queryIndexes: Post[];
  };

  const articleSection = (resources: ResourcesType[]) => {
    return resources.map((resource, index) => {
      return (
        <div
          data-testid={`article-section`}
          className={styles.articlesSection}
          // eslint-disable-next-line react/no-array-index-key
          key={`${resource.header}Section${index}`}
        >
          <div className="container">
            <div className="row justify-content-between">
              <div className="col-md-9">
                <div className={styles.categoryHeaderButtonContainer}>
                  <h3 className="heading4 expressive">{resource.header}</h3>
                </div>
              </div>
              <div className="col-md-3 widthFitContent visibleLgOrGreater">
                <Button href={resource.link} variant="outlined">
                  View All
                </Button>
              </div>
            </div>
            <div className="row justify-content-center">
              {resource.queryIndexes.map(
                (queryIndex, i: React.Key | null | undefined) => (
                  <div
                    className={`col-xs-12 col-md-9 col-xl-${!i ? '6' : '3'}`}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`article${i}`}
                  >
                    <ArticleCard
                      aspectRatio={!i ? '16 / 9' : '4 / 3'}
                      blogPostData={{
                        post: queryIndex,
                      }}
                    />
                  </div>
                ),
              )}
            </div>
            <div className={styles.mobileCategoryButtonContainer}>
              <Button href={resource.link} variant="outlined">
                View All
              </Button>
            </div>
          </div>
        </div>
      );
    });
  };

  // Remove any posts that has one of the tags listed to prevent duplicates from appearing in both posts at the top and posts by category
  const filteredCategoryPosts: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

  data?.postsByCategory?.group?.forEach((category) => {
    const filteredCategory = { ...category };
    const filteredPosts = filteredCategory?.nodes.filter((node) => {
      return !node.tags?.nodes.some((tag) =>
        ['editor-pick', 'popular-post', 'featured'].includes(tag.slug),
      );
    });
    if (filteredPosts.length > 0) {
      filteredCategory.nodes = filteredPosts.splice(0, 3);
      filteredCategoryPosts.push(filteredCategory);
    }
  });

  const articlesCategoriesResources = filteredCategoryPosts
    .sort((first, second) => second.totalCount - first.totalCount)
    .map((category) => {
      const { fieldValue, nodes } = category;
      const link = nodes[0].categories.nodes.find(
        (node: any) => node.name === fieldValue, // eslint-disable-line @typescript-eslint/no-explicit-any
      ).link;
      return {
        header: fieldValue,
        link,
        queryIndexes: nodes as Post[],
      };
    });

  const contentGroup1 = articleSection(
    articlesCategoriesResources.splice(0, 3),
  );
  const contentGroup2 = articleSection(articlesCategoriesResources);

  const popularPostsCarouselSlides = popularPosts.map((post) => {
    return (
      <ArticleCard
        key={post?.title}
        aspectRatio="1 / 1"
        blogPostData={{ post: post as Post }}
      />
    );
  });

  const mainArticle = (
    <ArticleCard
      largeCard
      isFeatured
      aspectRatio="16 / 9"
      blogPostData={{
        post: featuredPost as Post,
      }}
    />
  );

  const searchBarProps = {
    title: 'Insights for Investors',
    query,
    setQuery,
    location,
    searching,
    setSearching,
    resultsCountDisplay: !!results.length && (
      <ResultsCountDisplay
        className={styles.resultsCountContainer}
        count={results.length}
        searchString={query}
      />
    ),
    resultsCount: results.length,
    inputValue: input,
    setInputValue: setInput,
  };

  const mainArticlesView = () => {
    return (
      <>
        <Seo post={data.post} />
        <div className={`container ${styles.blogTitleContainer}`}>
          <h2 className="expressive">Insights for Investors</h2>
        </div>

        <div className="visibleLgOrLesser">
          <BlogFilter searchBarProps={{ ...searchBarProps }} />
        </div>
        <div className="container">
          <div className={`row ${styles.heroArticles} justify-content-center`}>
            <div className="col-xl-3 visibleXlOrGreater">
              <BlogFilter searchBarProps={{ ...searchBarProps }} />
            </div>

            <div className="col-md-9 col-xl-5">{mainArticle}</div>
            <div className="col-md-9 col-xl-2">
              <ArticleCard
                blogPostData={{
                  post: editorsPickPosts[0] as Post,
                }}
              />
            </div>
            <div className="col-md-9 col-xl-2">
              <ArticleCard
                blogPostData={{
                  post: editorsPickPosts[1] as Post,
                }}
              />
            </div>
          </div>
          {popularPostsCarouselSlides?.length > 0 && (
            <div className="row visibleLgOrGreater">
              <div className={`col ${styles.carouselWrapper}`}>
                <h3 className="heading3 expressive">Popular Posts</h3>
                <Carousel
                  slides={popularPostsCarouselSlides}
                  slidesPerView={
                    popularPostsCarouselSlides.length > 3 ? 3.5 : 3
                  }
                  leftFade={false}
                  rightFade={popularPostsCarouselSlides.length > 3}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.articlesContainer}>
          {contentGroup1}
          <div className="container">
            <div className={`${styles.signUpSection} darkContainer`}>
              <div className={styles.leftHex}>
                <SvgProjector
                  viewBox="0 0 122 136"
                  fileUrl="images/hex"
                  hasCustomSize
                />
              </div>
              <div>
                <div className={styles.signUpSectionCopy}>
                  {userData.name ? (
                    <>
                      <h4 className="heading5">
                        Discover Your Next Investment
                      </h4>
                      <span className="subHeading">
                        Browse real estate investments to fit your portfolio.
                      </span>
                    </>
                  ) : (
                    <>
                      <h4 className="heading5">Sign Up to Unlock More</h4>
                      <span className="subHeading">
                        Creating an account takes less than 5 minutes.
                      </span>
                    </>
                  )}
                </div>
                {userData.name ? (
                  <Button
                    href={Routes.viewInvestments.url}
                    className={styles.signUpButton}
                    variant="outlinedWhite"
                  >
                    {Routes.viewInvestments.name}
                  </Button>
                ) : (
                  <Button
                    href={Routes.logIn.url}
                    target="_blank"
                    variant="outlinedWhite"
                    className={styles.signUpButton}
                  >
                    {Routes.logIn.name}
                  </Button>
                )}
              </div>
              <div className={styles.rightHex}>
                <SvgProjector
                  viewBox="0 0 122 136"
                  fileUrl="images/hex"
                  hasCustomSize
                />
              </div>
            </div>
          </div>
          {contentGroup2}
        </div>

        <Contact />
      </>
    );
  };

  return (
    <Layout>
      <div className={styles.mainPageContent}>
        {query && (
          <SearchResults
            results={results}
            query={query}
            searchBarProps={searchBarProps}
            setQuery={setQuery}
            setInput={setInput}
          />
        )}
        {!query && mainArticlesView()}
      </div>
    </Layout>
  );
};

export default BlogTemplate;

export const pageQuery = graphql`
  fragment PostDataFragment on WpPost {
    title
    tags {
      nodes {
        slug
        name
      }
    }
    postFlags {
      flag
    }
    link
    categories {
      nodes {
        name
        link
      }
    }
    date(formatString: "MMM DD, YYYY")
  }

  query BlogById(
    # these variables are passed in via createBlog.pageContext in gatsby-node.js
    $id: String!
    $hidePreview: Boolean
  ) {
    # selecting the current post by id
    post: wpPage(id: { eq: $id }) {
      seo {
        ...SeoFragment
      }
      ldJsonSchema {
        multipleSchemas {
          schemaStringContent
        }
        schemaStringContent
      }
      id
      content
      title
      uri
    }

    localSearchPosts {
      publicIndexURL
      publicStoreURL
    }

    allWpPost(
      limit: 10
      sort: { date: DESC }
      filter: { enablePreview: { enablePreview: { ne: $hidePreview } } }
    ) {
      nodes {
        ...PostDataFragment
        featuredImage {
          node {
            altText
            gatsbyImage(width: 720, placeholder: BLURRED)
          }
        }
      }
    }

    postsByCategory: allWpPost(
      sort: { date: DESC }
      filter: {
        categories: {
          nodes: {
            elemMatch: { slug: { ne: "investor-stories" }, count: { gte: 3 } }
          }
        }
        enablePreview: { enablePreview: { ne: $hidePreview } }
      }
    ) {
      group(field: { categories: { nodes: { name: SELECT } } }, limit: 6) {
        fieldValue
        totalCount
        nodes {
          ...PostDataFragment
          featuredImage {
            node {
              altText
              gatsbyImage(width: 720, placeholder: BLURRED)
            }
          }
        }
      }
    }

    featuredPost: allWpPost(
      limit: 1
      filter: {
        tags: { nodes: { elemMatch: { slug: { eq: "featured" } } } }
        enablePreview: { enablePreview: { ne: $hidePreview } }
      }
    ) {
      nodes {
        ...PostDataFragment
        featuredImage {
          node {
            altText
            gatsbyImage(width: 1280, placeholder: BLURRED)
          }
        }
      }
    }

    editorsPickPosts: allWpPost(
      limit: 2
      filter: {
        tags: { nodes: { elemMatch: { slug: { eq: "editor-pick" } } } }
        enablePreview: { enablePreview: { ne: $hidePreview } }
      }
    ) {
      nodes {
        ...PostDataFragment
        featuredImage {
          node {
            altText
            gatsbyImage(width: 720, placeholder: BLURRED)
          }
        }
      }
    }

    popularPosts: allWpPost(
      filter: {
        tags: { nodes: { elemMatch: { slug: { eq: "popular-post" } } } }
        enablePreview: { enablePreview: { ne: $hidePreview } }
      }
    ) {
      nodes {
        ...PostDataFragment
        featuredImage {
          node {
            altText
            gatsbyImage(width: 720, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
